// Hero D „schemat metra” (paczka HD): jeden utwór → seria editów → trzy linie kont → publikacje
// z dniem i godziną, a przewijanie oddala kamerę aż do ~100 kont (rundy 41-D i 41-D-R2).
//
// Okno mapy (`.mp-fm-win`) stoi i przycina; w nim wrapper `.mp-fm-fit` (statyczna skala niskiego
// okna) i JEDNA warstwa transformowana `.mp-fm-cam`. W warstwie: SVG świata z danych generatora
// (`landingTree.data.ts`: 100 linii, przystanki jako odcinki zerowej długości, jedna ścieżka na
// stan na konto) i węzły DOM: pigułki utworu i serii, trzy konta D z dziewięcioma przystankami,
// etykieta „dziś” i konta dochodzące przed progiem z3 (26 w pionie, 33 w poziomie).
//
// `className` okna jest STATYCZNY (`mp-fm-win` + orientacja). Klasy zmienne (`intro`, `mp-fm-cut`,
// `z1..z3`, `mp-fm-sda` / `mp-fm-js`) i `.on` na kontach przełącza wyłącznie `useHeroZoom` przez
// `classList`; zmiana orientacji montuje okno od nowa (`key`), więc hook dostaje świeże węzły.
//
// Statyczny HTML strony głównej (snapshot prerendera, dogrywka HD2) niesie OBA okna mapy z klasą
// `mp-fm-snap`, a widać to, które wybiera zapytanie mediów. Te okna stoją w dokumencie tylko do
// pierwszego zatwierdzenia Reacta (`createRoot` je podmienia), więc mapa sprawdza je raz,
// w inicjalizatorze stanu: jeśli obraz już stoi, montaż przejmuje stan p = 0 bez ruchu wejścia.
//
// Mapa to JEDEN obraz dla czytnika (`role="img"` + `landing.hero.mapAlt`), wnętrze `aria-hidden`.
// Druga, statyczna mapa `ptak` (p = 1, bez tekstu, cała `aria-hidden`) stoi pod hero tylko przy
// ograniczonym ruchu i nie ma hooka: kamera, `z1 z2 z3` i `.on` idą prosto z renderu.

import { useMemo, useRef, useState } from 'react'
import type { CSSProperties, RefObject } from 'react'
import { useI18n } from '../../lib/i18n'
import { editWord } from '../../locales/plural'
import { PlatformLogo } from '../../ds/media/PlatformLogo'
import type { PlatformLogoName } from '../../ds/media/PlatformLogo'
import { LOD, camera } from '../../lib/landingTree'
import type { Orient, PlatformKey, TreeAccount, World } from '../../lib/landingTree'
import { WORLDS } from '../../lib/landingTree.data'
import { useHeroZoom } from '../../hooks/useHeroZoom'
import {
  D_ACCOUNTS, D_SERIES, D_STOPS, D_TODAY, D_TRACK, SERIES_EDITS, TRACK_TIME, TRACK_WAVE, kadrSrc,
} from './landingFlowMap.data'
import type { MapPoint } from './landingFlowMap.data'
import '../../styles/landing-metro.css'

const LOGO: Record<PlatformKey, PlatformLogoName> = { tt: 'tiktok', ig: 'instagram', yt: 'youtube' }
const NO_REF: RefObject<HTMLElement | null> = { current: null }
const r1 = (v: number) => Math.round(v * 10) / 10

/** Czy statyczny HTML namalował już mapę (okna snapshotu prerendera przed montażem Reacta). */
const snapshotPainted = () => typeof document !== 'undefined' && document.querySelector('.mp-fm-snap') !== null

/** Zmienne CSS węzła: położenie w pikselach mapy (+ indeks kolejki ruchu wejścia). */
const at = (p: MapPoint, i?: number) => ({ '--x': p.x, '--y': p.y, ...(i === undefined ? {} : { '--i': i }) }) as CSSProperties

function Avatar({ kadr, pf }: { kadr: string; pf: PlatformKey }) {
  return (
    <span className="mp-fm-av">
      <img src={kadrSrc(kadr)} alt="" width="44" height="44" decoding="async" />
      <span className="mp-fm-plg"><PlatformLogo name={LOGO[pf]} /></span>
    </span>
  )
}

/** Linia konta z przystankami; konto D ma głowę rysowaną ruchem wejścia i ogon za oknem p = 0. */
function AccountLines({ a, on }: { a: TreeAccount; on: boolean }) {
  return (
    <g className={`mp-fm-acc${a.d ? ' is-d' : ''}${on ? ' on' : ''}`} data-i={a.d ? undefined : a.i}>
      {a.d ? <path className={`mp-fm-ln is-head is-c${a.c}`} pathLength={1} d={a.head} style={{ '--i': a.c - 1 } as CSSProperties} /> : null}
      <path className={`mp-fm-ln is-c${a.c}`} d={a.line} />
      {a.bg ? <path className="mp-fm-sd is-bg" d={a.bg} /> : null}
      {a.ok ? <path className="mp-fm-sd is-ok" d={a.ok} /> : null}
      {a.sch ? <path className="mp-fm-sd is-sch" d={a.sch} /> : null}
      {a.wt.map(([x, y]) => <circle key={`${x} ${y}`} className="mp-fm-wr" cx={x} cy={y} r={4} />)}
      <path className={`mp-fm-wn is-c${a.c}`} d={a.wn} />
    </g>
  )
}

/** Zawartość warstwy kamery. `text` = false: mapa `ptak` bez węzłów tekstowych. */
function MapBody({ world, orient, text, allOn }: { world: World; orient: Orient; text: boolean; allOn: boolean }) {
  const { t, locale } = useI18n()
  const days = t('landing.hero.map.days').split(' · ')
  const [x0, y0, x1, y1] = world.bb
  const extra = world.accounts.filter((a) => !a.d)
  return (
    <div className="mp-fm-map">
      <svg className="mp-fm-world" viewBox={`${r1(x0)} ${r1(y0)} ${r1(x1 - x0)} ${r1(y1 - y0)}`} focusable="false"
           style={{ left: r1(x0), top: r1(y0), width: r1(x1 - x0), height: r1(y1 - y0) }}>
        <path className="mp-fm-ln is-today" d={world.today} />
        <path className="mp-fm-ln is-today is-far" d={world.todayX} />
        <path className="mp-fm-ln is-trunk" pathLength={1} d={world.trunk} />
        {extra.map((a) => <AccountLines key={a.i} a={a} on={allOn} />)}
        {world.accounts.filter((a) => a.d).map((a) => <AccountLines key={a.i} a={a} on={false} />)}
      </svg>
      <div className="mp-fm-n mp-fm-track" style={at(D_TRACK[orient])}>
        {text ? <span className="mp-fm-track-name">{t('landing.hero.map.track')}</span> : null}
        <span className="mp-fm-wave">
          {TRACK_WAVE.map((h, i) => <i key={i} style={{ height: `${h}%`, '--i': i } as CSSProperties} />)}
        </span>
        {text ? <span className="mp-fm-time">{TRACK_TIME}</span> : null}
      </div>
      <div className="mp-fm-n mp-fm-series" style={at(D_SERIES[orient])}>
        <span className="mp-fm-ks">
          {D_SERIES.kadry.map((k) => <img key={k} src={kadrSrc(k)} alt="" width="20" height="36" decoding="async" />)}
        </span>
        {text ? (
          <span className="mp-fm-series-n">{t('landing.hero.map.series', { n: SERIES_EDITS, edits: editWord(locale, SERIES_EDITS) })}</span>
        ) : null}
      </div>
      {D_ACCOUNTS.map((a, k) => (
        <div key={a.nick} className="mp-fm-n mp-fm-acct" style={at(a[orient], k)}>
          <Avatar kadr={a.kadr} pf={a.pf} />
          {text ? <b>{a.nick}</b> : null}
        </div>
      ))}
      {D_STOPS.map((s) => (
        <div key={`${s.acc}-${s.day}`} className={`mp-fm-n mp-fm-stop is-${s.state}`} style={at(s[orient], s.order)}>
          <img className="mp-fm-kadr" src={kadrSrc(s.kadr)} alt="" width="22" height="39" decoding="async" />
          <i className="mp-fm-dot" />
          {text ? <small className="mp-fm-when">{`${days[s.day]} ${s.time}`}</small> : null}
        </div>
      ))}
      {text ? <div className="mp-fm-n mp-fm-today" style={at(D_TODAY[orient])}>{t('landing.hero.map.today')}</div> : null}
      {extra.filter((a) => a.rev < LOD[2]).map((a) => (
        <div key={a.i} data-i={a.i} className={`mp-fm-n mp-fm-acct mp-fm-wx${allOn ? ' on' : ''}`}
             style={at({ x: a.x, y: r1(a.y - world.nodeR) })}>
          <Avatar kadr={a.kadr} pf={a.pf} />
          {text && a.nick ? <b>{a.nick}</b> : null}
        </div>
      ))}
    </div>
  )
}

export interface LandingFlowMapProps {
  /** Układ poziomy (≥ 1336 px): mapa pozioma 760 × 520; inaczej pionowa 390 × 300. */
  wide: boolean
  /** Pas przypięcia i ekran przypięty: z nich hook liczy postęp przewinięcia. */
  pin?: RefObject<HTMLElement | null>
  screen?: RefObject<HTMLElement | null>
  /** Ruch dozwolony: kamera sterowana przewijaniem i ruch wejścia. */
  zoom?: boolean
  /** Widok z lotu ptaka (p = 1) pod hero przy ograniczonym ruchu: bez tekstu, bez hooka. */
  ptak?: boolean
}

export default function LandingFlowMap({ wide, pin, screen, zoom = false, ptak = false }: LandingFlowMapProps) {
  const { t } = useI18n()
  const orient: Orient = wide ? 'h' : 'v'
  const world = WORLDS[orient]
  const win = useRef<HTMLDivElement>(null)
  const cam = useRef<HTMLDivElement>(null)
  const fit = useRef<HTMLDivElement>(null)
  const refs = useMemo(() => ({ pin: pin ?? NO_REF, screen: screen ?? NO_REF, win, cam, fit }), [pin, screen])
  const [painted] = useState(snapshotPainted)
  useHeroZoom(refs, world, zoom && !ptak && Boolean(pin) && Boolean(screen), !painted)

  if (ptak) {
    return (
      <div key={orient} className={`mp-fm-win mp-fm-${orient} mp-fm-ptak z1 z2 z3`} aria-hidden="true">
        <div className="mp-fm-fit">
          <div className="mp-fm-cam" style={{ transform: camera(world, 1).css }}>
            <MapBody world={world} orient={orient} text={false} allOn />
          </div>
        </div>
      </div>
    )
  }
  return (
    <div key={orient} ref={win} className={`mp-fm-win mp-fm-${orient}`} role="img" aria-label={t('landing.hero.mapAlt')}>
      <div className="mp-fm-fit" ref={fit}>
        <div className="mp-fm-cam" ref={cam} aria-hidden="true">
          <MapBody world={world} orient={orient} text allOn={false} />
        </div>
      </div>
    </div>
  )
}
